import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  OnAgentThinking,
  thinkingHintFromMessages,
} from '@/components/core';
import { COMMON_AGENT_MESSAGES } from '@/constant';
import { createChatMessage, tryCatch } from '@/lib/utils';
import { MessageResponse, SessionHistoryResponse } from '@/services/sessions';
import { Agent } from '@/types/agents';
import {
  CHAT_CHANNEL_TYPE,
  CHAT_MESSAGE_RATING,
  CHAT_USER_ROLE,
  ChatMessage,
} from '@/types/chat';

type UseAgentChatCommonArgs = {
  onMessage?: (message: ChatMessage) => void;
  onThinking?: OnAgentThinking;
  agent: Agent | undefined;
  sessionHistory?: SessionHistoryResponse;
  sessionId: string | undefined | null;
  isNewSession?: boolean;
  updateChatState: (state: {
    isNewSession?: boolean;
    sessionId?: string | null;
  }) => void;
  sendChatMessage: (message: string) => Promise<MessageResponse | undefined>;
  refreshSessionHistory?: () => void;
};

const initialMessages = [
  createChatMessage({
    content: COMMON_AGENT_MESSAGES.greeting,
    type: CHAT_CHANNEL_TYPE.BOT,
  }),
];

const mapSessionHistoryMessages = (
  messages: SessionHistoryResponse['messages']
): ChatMessage[] => {
  const rating = (msg: { is_positive: boolean; is_negative: boolean }) => {
    if (msg.is_positive) return CHAT_MESSAGE_RATING.POSITIVE;
    if (msg.is_negative) return CHAT_MESSAGE_RATING.NEGATIVE;
    return null;
  };

  return messages
    .sort((a, b) => a.created_at - b.created_at)
    .map(msg =>
      createChatMessage({
        id: msg.id,
        rating: rating(msg),
        type:
          msg.role === CHAT_USER_ROLE.USER
            ? CHAT_CHANNEL_TYPE.HUMAN
            : CHAT_CHANNEL_TYPE.BOT,
        content: msg.body,
        timestamp: msg.created_at,
        tools: msg.tools,
      })
    );
};

// Shared chat behavior: message state, history mapping, and send flow.
const useAgentChatCommon = ({
  onMessage,
  onThinking,
  sessionId,
  isNewSession,
  updateChatState,
  sessionHistory,
  sendChatMessage,
  refreshSessionHistory,
}: UseAgentChatCommonArgs) => {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [isChatActive, setIsChatActive] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isLoading) {
      onThinking?.(false);
    } else {
      onThinking?.(true, thinkingHintFromMessages(messages));
    }
    setIsChatActive(isLoading);
  }, [isLoading, messages, onThinking]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!sessionId) {
      updateChatState({
        isNewSession: true,
      });
      console.info('No session found, setting default message');
      setMessages(initialMessages);

      return;
    }

    if (isNewSession) {
      return;
    }

    setMessages([
      createChatMessage({
        content: 'Loading...',
        type: CHAT_CHANNEL_TYPE.BOT,
        isLoader: true,
      }),
    ]);
  }, [sessionId, isNewSession, updateChatState]);

  useEffect(() => {
    if (!sessionHistory || isNewSession) return;
    setMessages(mapSessionHistoryMessages(sessionHistory.messages));
  }, [sessionHistory, isNewSession]);

  const sendMessage = useCallback(
    async (message: string) => {
      setIsLoading(true);

      const userMessage: ChatMessage = createChatMessage({
        type: CHAT_CHANNEL_TYPE.HUMAN,
        content: message,
      });

      const loadingMessage: ChatMessage = createChatMessage({
        type: CHAT_CHANNEL_TYPE.BOT,
        content: 'Thinking...',
        isLoader: true,
      });

      setMessages(prev => [...prev, userMessage, loadingMessage]);

      const messageResponse = await tryCatch(() => sendChatMessage(message));

      setMessages(prev => prev.filter(msg => msg.id !== loadingMessage.id));

      setIsLoading(false);

      if (messageResponse.error) {
        console.error('Error sending message:', messageResponse.error);
        const botMessage: ChatMessage = createChatMessage({
          type: CHAT_CHANNEL_TYPE.BOT,
          content: COMMON_AGENT_MESSAGES.tryAgain,
        });
        setMessages(prev => [...prev, botMessage]);
        return;
      }

      if (!messageResponse.data?.message || !messageResponse.data?.message_id) {
        const botMessage: ChatMessage = createChatMessage({
          isStreaming: true,
          type: CHAT_CHANNEL_TYPE.BOT,
          content: COMMON_AGENT_MESSAGES.couldNotProcess,
        });
        setMessages(prev => [...prev, botMessage]);
        onMessage?.(botMessage);

        return;
      }

      const messageData = messageResponse.data;

      if (messageData.session_id) {
        updateChatState({
          sessionId: messageData.session_id,
        });
        refreshSessionHistory?.();
      }

      const botMessage: ChatMessage = createChatMessage({
        id: messageData.message_id,
        isStreaming: true,
        type: CHAT_CHANNEL_TYPE.BOT,
        content: messageData.message,
        tools: messageData.tools,
      });
      setMessages(prev => [...prev, botMessage]);
      onMessage?.(botMessage);
    },
    [onMessage, refreshSessionHistory, sendChatMessage, updateChatState]
  );

  const handleLastMessageProcessCompleted = useCallback(() => {
    setIsChatActive(false);
  }, []);

  const updateMessageRating = useCallback(
    (messageId: string, rating: CHAT_MESSAGE_RATING) => {
      setMessages(prev =>
        prev.map(message =>
          message.id === messageId ? { ...message, rating } : message
        )
      );
    },
    []
  );

  return {
    messages,
    setMessages,
    updateMessageRating,
    setIsLoading,
    isLoading,
    isChatActive,
    sendMessage,
    handleLastMessageProcessCompleted,
    messagesEndRef,
    scrollToBottom,
  };
};

export type { UseAgentChatCommonArgs };
export { useAgentChatCommon };
